import React from 'react';
import PropTypes from 'prop-types';
import Events from './common/events';
import UpdateWatcher from './UpdateWatcher';
import analytics from 'APP_ROOT/services/analytics';


/**
 * The AutoReload Component uses an UpdateWatcher to tell it when an update is available.
 * When an update is available, window.reload() is called the next time the full screen
 * loading indicator is displayed or the user navigates to a new path.
 * */
export default class AutoReload extends React.PureComponent {

    constructor(props) {
        super(props);

        // Set the name of the hidden property and the change event for visibility
        // ref: https://developer.mozilla.org/en-US/docs/Web/API/Page_Visibility_API#Example
        if (typeof document.hidden !== "undefined") {
            this.PROPERTY_hidden = "hidden";
            this.EVENT_visibilityChange = "visibilitychange";
        } else if (typeof document.msHidden !== "undefined") {
            this.PROPERTY_hidden = "msHidden";
            this.EVENT_visibilityChange = "msvisibilitychange";
        } else if (typeof document.webkitHidden !== "undefined") {
            this.PROPERTY_hidden = "webkitHidden";
            this.EVENT_visibilityChange = "webkitvisibilitychange";
        }

        this.state = {
            updateWatcherActive: false,
            lastCheckTime: Date.now(),
            updateAvailable: false,
            loadingFullscreen: false,
        };
    }

    isDocumentHidden = () => this.PROPERTY_hidden && document[this.PROPERTY_hidden]

    handleVisibilityChange = () => {
        const documentHidden = this.isDocumentHidden();
        if (documentHidden === undefined) return;
        this.setState({
            updateWatcherActive: !documentHidden
        });
        if (documentHidden) {
            this.reloadIfUpdateAvailable('DocumentHidden');
        }
    }

    handleShowLoadingEvent = (event) => {
        if (event && event.detail && event.detail.safeToReload) {
            this.reloadIfUpdateAvailable('FullScreenLoading');
        }
    }

    handleHideLoadingEvent = () => {
        this.setState({
            loadingFullscreen: false
        });
    }

    componentDidMount() {
        if(this.EVENT_visibilityChange) {
            document.addEventListener(this.EVENT_visibilityChange, this.handleVisibilityChange, false);
        }
        document.addEventListener(Events.SHOW_LOADING_PAGE, this.handleShowLoadingEvent, false);
        document.addEventListener(Events.HIDE_LOADING_PAGE, this.handleHideLoadingEvent, false);
        this.handleVisibilityChange();
    }

    componentWillUnmount() {
        document.removeEventListener(this.EVENT_visibilityChange, this.handleVisibilityChange);
        document.removeEventListener(Events.SHOW_LOADING_PAGE, this.handleShowLoadingEvent);
        document.removeEventListener(Events.HIDE_LOADING_PAGE, this.handleHideLoadingEvent);
    }

    componentDidUpdate(oldProps) {
        if (oldProps.location.pathname !== this.props.location.pathname) {
            this.reloadIfUpdateAvailable('Navigation');
        }
    }

    onCheckResult = updateFound => {
        this.setState({
            lastCheckTime: Date.now(),
            updateAvailable: updateFound
        });
    }

    reloadIfUpdateAvailable = cause => {
        if (this.state.updateAvailable) {
            analytics.event({
                category: analytics.EventCategory.Updates,
                action: analytics.EventAction.AutoReload,
                label: cause,
            });
            // for tests - we want to render the reloading div
            this.setState({ reloading: true });
            window.location.reload();
        }
    }

    render() {
        const {
            updateWatcherActive,
            lastCheckTime,
            updateAvailable,
            reloading,
        } = this.state;

        // Fulfill your purpose AutoReload!
        if (reloading) {
            return <div data-testid='reloading' />;
        }
        if (updateAvailable) {
            return <div data-testid='pending-reload' />;
        }

        return (
            <UpdateWatcher
                active={updateWatcherActive}
                version={this.props.version}
                lastCheckTime={lastCheckTime}
                onCheckResult={this.onCheckResult}
            />
        );
    }
}

AutoReload.propTypes = {
    version: PropTypes.string,
    location: PropTypes.shape({
        pathname: PropTypes.string.isRequired
    }).isRequired
};

AutoReload.defaultProps = {
    version: process.env.REACT_APP_VERSION
};
